/** Pure helpers: persisted graph (no layout) + graphMeta (positions, viewport). */

export function defaultGraph() {
  return { nodes: [], edges: [] };
}

export function defaultGraphMeta() {
  return { positions: {}, viewport: null };
}

/** Map legacy vertical handles to Bleu-style horizontal pin ids. */
export function normalizeEdgeHandles(edge) {
  const e = { ...edge };
  const sh = e.sourceHandle;
  const th = e.targetHandle;
  if (sh === 'out') e.sourceHandle = 'flow-out';
  else if (sh === 'in') e.sourceHandle = 'flow-in';
  else if (sh === undefined || sh === null) e.sourceHandle = 'flow-out';
  if (th === 'in') e.targetHandle = 'flow-in';
  else if (th === 'out') e.targetHandle = 'flow-out';
  else if (th === undefined || th === null) e.targetHandle = 'flow-in';
  return e;
}

export function mergeGraphWithMeta(graph, graphMeta) {
  const g = graph && typeof graph === 'object' ? graph : defaultGraph();
  const meta = graphMeta && typeof graphMeta === 'object' ? graphMeta : defaultGraphMeta();
  const positions = meta.positions && typeof meta.positions === 'object' ? meta.positions : {};

  const nodes = (Array.isArray(g.nodes) ? g.nodes : []).map((n, i) => {
    const pos = positions[n.id];
    return {
      id: n.id,
      type: n.type || 'wfAction',
      position: pos && typeof pos.x === 'number' && typeof pos.y === 'number' ? pos : { x: 80 + (i % 5) * 40, y: 80 + i * 12 },
      data: n.data && typeof n.data === 'object' ? { ...n.data } : {}
    };
  });

  const edges = (Array.isArray(g.edges) ? g.edges : []).map((e) => {
    const ne = normalizeEdgeHandles({
      id: e.id,
      type: e.type || 'bleu',
      source: e.source,
      target: e.target,
      sourceHandle: e.sourceHandle,
      targetHandle: e.targetHandle,
      animated: e.animated === false ? false : true
    });
    return ne;
  });

  return { nodes, edges, viewport: meta.viewport || null };
}

export function splitGraphMeta(nodes, edges, viewport) {
  const positions = {};
  const slimNodes = nodes.map((n) => {
    positions[n.id] = { x: n.position.x, y: n.position.y };
    return {
      id: n.id,
      type: n.type,
      data: n.data && typeof n.data === 'object' ? { ...n.data } : {}
    };
  });

  const slimEdges = edges.map((e) => {
    const o = {
      id: e.id,
      source: e.source,
      target: e.target
    };
    if (e.sourceHandle) o.sourceHandle = e.sourceHandle;
    if (e.targetHandle) o.targetHandle = e.targetHandle;
    return o;
  });

  return {
    graph: { nodes: slimNodes, edges: slimEdges },
    graphMeta: {
      positions,
      viewport:
        viewport && typeof viewport.x === 'number' && typeof viewport.y === 'number' && typeof viewport.zoom === 'number'
          ? { x: viewport.x, y: viewport.y, zoom: viewport.zoom }
          : null
    }
  };
}

export function newNodeId() {
  return `n-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function newEdgeId() {
  return `e-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** For connection validation: flow vs data vs tool refs (for AI Tools → Text Generation). */
export function pinBus(handleId) {
  const h = handleId || '';
  if (h === 'in' || h === 'out') return 'flow';
  if (h === 'tools-in') return 'tool';
  if (h.startsWith('tool-')) return 'tool';
  if (h.startsWith('data-')) return 'data';
  if (h.startsWith('flow-')) return 'flow';
  return 'flow';
}

/** When creating a node from a dropped wire, pick target handle on the new node. */
export function targetHandleForDroppedWire(sourceHandleId, newNodeType) {
  if (newNodeType === 'wfTrigger') return null;
  if (newNodeType?.startsWith('wfEventTg')) return 'flow-in';
  const src = sourceHandleId || 'flow-out';
  if (src.startsWith('tool-')) return 'tools-in';
  const dataWire = src.startsWith('data-');
  return dataWire ? 'data-in' : 'flow-in';
}
